import React, { useState, useEffect, useCallback } from 'react';
import { Container, Typography, Paper, Breadcrumbs, Link as MuiLink, Grid, TextField, Button, CircularProgress, Alert, Switch, FormControlLabel, Box, MenuItem, Slider } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { supabase } from '../../../supabaseClient';

const UserSettingsPage = () => {
  const { userId } = useParams();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // State for model options from database
  const [embeddingModels, setEmbeddingModels] = useState([]);
  const [llmModels, setLlmModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);

  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }, []);

  // Fetch embedding and LLM models from database
  useEffect(() => {
    const fetchModels = async () => {
      setLoadingModels(true);
      try {
        // Fetch embedding models
        const { data: embeddingData, error: embeddingError } = await supabase
          .from('embedding_models')
          .select('model_identifier, display_label, provider')
          .eq('is_active', true)
          .order('provider', { ascending: true })
          .order('display_label', { ascending: true });

        if (embeddingError) throw embeddingError;
        setEmbeddingModels(embeddingData || []);

        // Fetch LLM models
        const { data: llmData, error: llmError } = await supabase
          .from('llm_models')
          .select('model_identifier, display_label, provider')
          .eq('is_active', true)
          .order('provider', { ascending: true })
          .order('display_label', { ascending: true });

        if (llmError) throw llmError;
        setLlmModels(llmData || []);
      } catch (err) {
        console.error('Error fetching models:', err);
        setError(err.message);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAuthToken();
        if (!token) throw new Error("Authentication token not available.");

        const response = await fetch(`/functions/v1/admin-user-settings-manager/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'apikey': (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY },
        });
        if (!response.ok) throw new Error('Failed to fetch user settings.');

        const data = await response.json();
        setSettings(data || {});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [userId, getAuthToken]);

  const handleChange = (e, sliderValue) => {
    const { name, value, type, checked } = e.target;

    let newValue;
    if (name === 'temperature' || name === 'grounding_similarity_threshold') {
      newValue = sliderValue !== undefined ? sliderValue : parseFloat(value);
    } else if (type === 'checkbox') {
      newValue = checked;
    } else {
      newValue = value;
    }

    setSettings(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication token not available.");

      const response = await fetch(`/functions/v1/admin-user-settings-manager/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'apikey': (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to save settings.');
      }

      setSaveSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || loadingModels) return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Container>;


  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/admin">
          Admin Panel
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to={`/admin/user/${userId}`}>
          User Details
        </MuiLink>
        <Typography color="text.primary">Default Settings</Typography>
      </Breadcrumbs>
      <Typography variant="h4" gutterBottom>
        Manage Default User Settings
      </Typography>
      <Paper sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>Settings saved successfully!</Alert>}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              name="embedding_model"
              label="Embedding Model"
              value={settings.embedding_model || ''}
              onChange={handleChange}
              fullWidth
              disabled={saving}
            >
              {embeddingModels.map((model) => (
                <MenuItem key={model.model_identifier} value={model.model_identifier}>
                  {model.display_label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              name="llm_model"
              label="LLM Model"
              value={settings.llm_model || ''}
              onChange={handleChange}
              fullWidth
              disabled={saving}
            >
              {llmModels.map((model) => (
                <MenuItem key={model.model_identifier} value={model.model_identifier}>
                  {model.display_label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField name="system_prompt" label="System Prompt" value={settings.system_prompt || ''} onChange={handleChange} fullWidth multiline rows={4} />
          </Grid>
          <Grid item xs={12}>
            <TextField name="direct_system_prompt" label="Direct System Prompt" value={settings.direct_system_prompt || ''} onChange={handleChange} fullWidth multiline rows={2} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ pl: 1, pr: 1, mt: 2 }}>
              <Typography gutterBottom>
                Creativity (Temperature): {Number(settings.temperature || 0).toFixed(1)}
              </Typography>
              <Slider
                name="temperature"
                aria-label="Temperature"
                value={Number(settings.temperature || 0)}
                onChange={handleChange}
                step={0.1}
                marks
                min={0.0}
                max={2.0}
                valueLabelDisplay="auto"
              />
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={3}><TextField type="number" name="top_p" label="Top P" value={settings.top_p || 0} onChange={handleChange} fullWidth /></Grid>
          <Grid item xs={6} sm={4} md={3}><TextField type="number" name="total_chunks" label="Total Chunks" value={settings.total_chunks || 0} onChange={handleChange} fullWidth /></Grid>
          <Grid item xs={6} sm={4} md={3}><TextField type="number" name="answer_max_token" label="Answer Max Tokens" value={settings.answer_max_token || 0} onChange={handleChange} fullWidth /></Grid>
          <Grid item xs={6} sm={4} md={3}><TextField type="number" name="top_k" label="Top K" value={settings.top_k || 0} onChange={handleChange} fullWidth /></Grid>
          <Grid item xs={6} sm={4} md={3}><TextField type="number" name="cosine_similarity_threshold" label="Cosine Similarity Threshold" value={settings.cosine_similarity_threshold || 0} onChange={handleChange} fullWidth /></Grid>
          <Grid item xs={6} sm={4} md={3}>
            <TextField name="rag_mode" label="RAG Strategy" value={settings.rag_mode || 'similarity_filter'} onChange={handleChange} fullWidth select>
              <MenuItem value="similarity_filter">Similarity Filter</MenuItem>
              <MenuItem value="reranker">Reranker</MenuItem>
              <MenuItem value="two_stage_summary">Two-Stage Summary</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Switch name="enable_grounding_check" checked={settings.enable_grounding_check || false} onChange={handleChange} />} label="Enable Answer Grounding Check" />
            {settings.enable_grounding_check && (
              <Box sx={{ pl: 1, pr: 1, mt: 1 }}>
                <Typography gutterBottom>
                  Grounding Threshold: {Number(settings.grounding_similarity_threshold || 0).toFixed(2)}
                </Typography>
                <Slider
                  name="grounding_similarity_threshold"
                  aria-label="Grounding Similarity Threshold"
                  value={Number(settings.grounding_similarity_threshold || 0)}
                  onChange={handleChange}
                  step={0.05}
                  marks
                  min={0.0}
                  max={1.0}
                  valueLabelDisplay="auto"
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sx={{ pt: 0 }}>
            <FormControlLabel control={<Switch name="enable_reranker" checked={settings.enable_reranker || false} onChange={handleChange} />} label="Enable Reranker" />
          </Grid>

          {/* AI Tools Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>AI Tools</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable or disable AI tools for this user's chat integrations.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Switch
                  name="tool_source_attribution"
                  checked={settings.enabled_tools?.source_attribution !== false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      enabled_tools: {
                        ...prev.enabled_tools,
                        source_attribution: e.target.checked
                      }
                    }));
                  }}
                />
              }
              label="Source Attribution"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Switch
                  name="tool_web_search"
                  checked={settings.enabled_tools?.web_search || false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      enabled_tools: {
                        ...prev.enabled_tools,
                        web_search: e.target.checked
                      }
                    }));
                  }}
                />
              }
              label="Web Search"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Switch
                  name="requires_approval"
                  checked={settings.requires_approval || false}
                  onChange={handleChange}
                />
              }
              label="Require approval before sending answers"
            />
          </Grid>

          {/* E-Commerce Tools */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              E-Commerce Tools (IKAS)
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Switch
                  name="tool_ikas_order_lookup"
                  checked={settings.enabled_tools?.ikas_order_lookup || false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      enabled_tools: {
                        ...prev.enabled_tools,
                        ikas_order_lookup: e.target.checked
                      }
                    }));
                  }}
                />
              }
              label="Order Lookup"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Switch
                  name="tool_ikas_stock_check"
                  checked={settings.enabled_tools?.ikas_stock_check || false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      enabled_tools: {
                        ...prev.enabled_tools,
                        ikas_stock_check: e.target.checked
                      }
                    }));
                  }}
                />
              }
              label="Stock Check"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Switch
                  name="tool_ikas_customer_lookup"
                  checked={settings.enabled_tools?.ikas_customer_lookup || false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      enabled_tools: {
                        ...prev.enabled_tools,
                        ikas_customer_lookup: e.target.checked
                      }
                    }));
                  }}
                />
              }
              label="Customer Lookup"
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
                Save Settings
              </Button>
              {saving && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default UserSettingsPage; 